import { cardCoverFrame, type CardCoverSpec } from './cardCovers';
import {
  cardText,
  cardTypeCss,
  cardWatermark,
  fitCardText,
  round2,
  type KitBannerCard,
  type KitBannerSite,
  type KitCardFont,
  type KitCardItem,
  type KitCardLayout,
  type KitCardLayoutInput,
} from './kitCard';
import { STORY_FRAMES, storyThemeIdOf } from './storyFrames';
import type { StoryBox } from './storyLayout';
import { groundDots, mixHex, storyAlpha, TORN_TOP_PATH, type StoryColours, type StoryCss } from './storyStyles';
import { WATERMARK_PLACEMENTS } from './watermark';

/**
 * The profile banners (design `asset-redesign/g Banners - new`, `BannerFrame.dc.html`, owner answers
 * 2026-10-08): one evergreen banner per site. A centred row in the content box of the site: the
 * cover in the frame of the theme, then a text column with the status ("Out Fri 16 Oct", then "Out
 * now"), the title one time, the artist and the SmartLink ("Listen on …"; no URL on Facebook). Every
 * size is the YouTube size times the unit of the site (`u`). Pure.
 */

export interface BannerSiteSpec {
  site: KitBannerSite;
  size: { width: number; height: number };
  /** The content box: the safe area of the site less a margin, clear of the profile photo. */
  box: StoryBox;
  /** The unit of the site: YouTube 1, X 0.8, Facebook 0.5. */
  unit: number;
  /** False: no URL line (Facebook: the mobile crop shows the file about 390 px wide). */
  url: boolean;
}

/** The sites (page g, "Build spec for the Remotion layouts"; the crop numbers of the request, owner answer 5). */
export const BANNER_SITES: Readonly<Record<KitBannerSite, BannerSiteSpec>> = {
  // Safe area 1546 x 423 at x 507, y 508 (every device); the content box keeps 24 px inside it.
  youtube: {
    site: 'youtube',
    size: { width: 2560, height: 1440 },
    box: { x: 531, y: 532, w: 1498, h: 375 },
    unit: 1,
    url: true,
  },
  // Mobile cuts about 60 px at the top and the bottom; x from 420 clears the x.com desktop photo.
  x: { site: 'x', size: { width: 1500, height: 500 }, box: { x: 420, y: 80, w: 1020, h: 340 }, unit: 0.8, url: true },
  // The mobile 16:9 crop (x 146 to 706), clear of the profile picture (x 24 to 194 from y 230).
  facebook: {
    site: 'facebook',
    size: { width: 851, height: 315 },
    box: { x: 204, y: 24, w: 500, h: 267 },
    unit: 0.5,
    url: false,
  },
};

/** The site of a banner variant (`youtube`, `x`, `facebook`); null for any other value. */
export function bannerSiteOf(variant: string): KitBannerSite | null {
  return variant === 'youtube' || variant === 'x' || variant === 'facebook' ? variant : null;
}

/** The YouTube sizes of the banner (page g, "Sizes"), before the unit of the site and the factors of the theme. */
export const BANNER_SIZES = {
  cover: 340,
  /** The Handmade photo is smaller: its mat is taller than wide. */
  coverHandmade: 280,
  rowGap: 64,
  /** The room for the Retro record right of the sleeve. */
  retroRecordRoom: 70,
  columnGap: 12,
  columnGapHandmade: 20,
  status: 40,
  statusPadX: 28,
  statusPadY: 10,
  statusBorder: 4,
  title: 128,
  artist: 52,
  url: 32,
  noirLine: { w: 120, h: 3 },
} as const;

/** The line heights of the design: display 0.92, text 1.1. */
const LINE = { display: 0.92, text: 1.1 } as const;
/** The smallest scale of the row when the column does not fit the content box. */
const MIN_SCALE = 0.6;

function coverSpec(u: number): CardCoverSpec {
  const px = (value: number): number => Math.round(value * u);
  return {
    clean: { radius: px(18), shadow: [px(30), px(50), -px(24)] },
    hype: { turn: 5, border: px(12), shadow: px(16) },
    dreamy: { radius: px(48), glow: [px(30), px(80), -px(24)] },
    dark: { pad: px(14), outline: 2, grey: 0.85 },
    warm: { radius: px(20), shadow: [px(30), px(60), -px(26)] },
    retro: {
      border: px(10),
      shadow: [px(22), px(40), -px(22)],
      record: { top: '8%', right: '-26%', width: '84%', inset: px(20) },
    },
    neon: { pad: px(10), border: px(5), radius: px(26), glow: px(24), inset: px(18), pictureRadius: px(14) },
    handmade: { pad: [px(12), px(12), px(44)], turn: -4, shadow: [px(22), px(36), -px(18)], tape: [-px(20), px(40)] },
  };
}

/** The colour of the title: the accent, or the text colour (Noir, Retro, Neon). */
function heroColour(themeId: string, colors: StoryColours): string {
  const id = storyThemeIdOf(themeId);
  return id === 'dark' || id === 'retro' || id === 'neon' ? colors.text : colors.accent;
}

/** The ground of the banner of each theme (`BannerFrame` `.bf-ground`). Null: a flat background. */
export function bannerGround(themeId: string, colors: StoryColours): string | null {
  const id = storyThemeIdOf(themeId);
  switch (id) {
    case 'clean':
      return `radial-gradient(ellipse 60% 70% at 50% 50%, ${storyAlpha(colors.surface, 0.8)}, transparent 75%)`;
    case 'hype':
      return `radial-gradient(ellipse 50% 60% at 50% 50%, ${storyAlpha(colors.accent, 0.22)}, transparent 70%)`;
    case 'dreamy':
      return [
        'radial-gradient(ellipse 50% 45% at 50% 0%, rgba(255,255,255,.5), transparent 70%)',
        `radial-gradient(ellipse 60% 50% at 50% 100%, ${storyAlpha(colors.accent, 0.25)}, transparent 70%)`,
        `linear-gradient(180deg, ${colors.ink} 0%, ${colors.surface} 100%)`,
      ].join(', ');
    case 'dark':
      return `${groundDots('rgba(255,255,255,.07)', 7, 1)}, radial-gradient(ellipse 45% 60% at 50% 50%, ${colors.surface}, transparent 75%)`;
    case 'warm':
      return `radial-gradient(ellipse 45% 70% at 40% 30%, ${storyAlpha(colors.accent, 0.3)}, transparent 70%), linear-gradient(180deg, ${colors.surface} 0%, ${colors.ink} 70%)`;
    case 'retro':
      return groundDots('rgba(255,255,255,.08)', 6, 1);
    case 'neon':
      return `radial-gradient(ellipse 40% 60% at 20% 30%, ${storyAlpha(colors.text, 0.26)}, transparent 70%), radial-gradient(ellipse 45% 60% at 85% 75%, ${storyAlpha(colors.accent, 0.26)}, transparent 70%)`;
    case 'handmade':
      return `${groundDots('rgba(80,50,30,.05)', 9, 1)}, radial-gradient(ellipse 60% 70% at 45% 45%, rgba(255,255,255,.5), transparent 75%)`;
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

const abs = (style: StoryCss): StoryCss => ({ position: 'absolute', ...style });

/**
 * The ornaments of the banner (`.bf-deco`): only outside the content box. The bottom ornaments
 * (Hype stripes, Retro arcs, Handmade strip) start at 35 to 45 % of the width, so the profile photo
 * corner stays calm. YouTube moves them to the edges of the desktop band.
 */
export function bannerDeco(themeId: string, colors: StoryColours, site: KitBannerSite): StoryCss[] {
  const id = storyThemeIdOf(themeId);
  const yt = site === 'youtube';
  switch (id) {
    case 'clean':
      return [abs({ left: 0, right: 0, top: '50%', height: 2, background: storyAlpha(colors.text, 0.1) })];
    case 'hype':
      return [
        abs({
          left: '-10%',
          width: '55%',
          top: yt ? '30%' : '-4%',
          height: yt ? '4%' : '9%',
          background: colors.surface,
          transform: 'rotate(-6deg)',
        }),
        abs({
          left: '45%',
          right: '-10%',
          bottom: yt ? '31%' : '-3%',
          height: yt ? '3.5%' : '8%',
          background: colors.surface,
          transform: 'rotate(-6deg)',
        }),
        abs({
          left: '45%',
          right: '-10%',
          bottom: yt ? '36.5%' : '8%',
          height: yt ? '.8%' : '1.6%',
          background: colors.accent,
          transform: 'rotate(-6deg)',
        }),
      ];
    case 'retro': {
      const arc = (top: string, background: string): StoryCss =>
        abs({ left: '40%', right: '-20%', top, height: '300%', borderRadius: '50%', background });
      return [
        arc(yt ? '66%' : '92%', mixHex(colors.text, colors.ink, 0.7)),
        arc(yt ? '67.4%' : '94%', colors.surface),
        arc(yt ? '68.8%' : '96%', colors.accent),
      ];
    }
    case 'handmade':
      return [
        abs({
          left: '35%',
          right: '-5%',
          bottom: yt ? '33%' : '-2%',
          height: yt ? '3%' : '9%',
          background: colors.surface,
          transform: 'rotate(-2deg)',
          clipPath: TORN_TOP_PATH,
        }),
      ];
    case 'dreamy':
    case 'dark':
    case 'warm':
    case 'neon':
      return [];
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

/** The look of the status ring: Hype a surface label turned -3°, Neon an accent tube, else a text ring. */
function statusCss(themeId: string, colors: StoryColours, u: number): StoryCss {
  const border = Math.round(BANNER_SIZES.statusBorder * u);
  const base: StoryCss = {
    color: colors.text,
    padding: `${Math.round(BANNER_SIZES.statusPadY * u)}px ${Math.round(BANNER_SIZES.statusPadX * u)}px`,
    border: `${border}px solid currentColor`,
    borderRadius: 999,
  };
  const id = storyThemeIdOf(themeId);
  if (id === 'hype') {
    return {
      ...base,
      background: colors.surface,
      color: colors.accent,
      borderColor: colors.surface,
      transform: 'rotate(-3deg)',
    };
  }
  if (id === 'neon') {
    return {
      ...base,
      color: colors.accent,
      boxShadow: `0 0 ${Math.round(18 * u)}px ${colors.accent}`,
      textShadow: `0 0 ${Math.round(10 * u)}px ${colors.accent}`,
    };
  }
  return base;
}

/** The look of the title: the hero colour and the effect of the theme. */
function titleCss(themeId: string, colors: StoryColours, u: number): StoryCss {
  const id = storyThemeIdOf(themeId);
  const colour = heroColour(themeId, colors);
  switch (id) {
    case 'hype':
      return { color: colour, textShadow: `${Math.round(8 * u)}px ${Math.round(8 * u)}px 0 ${colors.surface}` };
    case 'retro':
      return { color: colour, textShadow: `${Math.round(6 * u)}px ${Math.round(6 * u)}px 0 ${colors.accent}` };
    case 'neon':
      return {
        color: colour,
        textShadow: `0 0 ${Math.round(12 * u)}px currentColor, 0 0 ${Math.round(34 * u)}px currentColor`,
      };
    case 'handmade':
      return { color: colour, transform: 'rotate(-3deg)' };
    case 'clean':
    case 'dreamy':
    case 'dark':
    case 'warm':
      return { color: colour };
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

/** The marker under each line of the Handmade title. Absent for every other theme. */
function titleLineCss(themeId: string, colors: StoryColours, u: number): StoryCss | undefined {
  if (storyThemeIdOf(themeId) !== 'handmade') {
    return undefined;
  }
  return {
    background: `linear-gradient(transparent 60%, ${storyAlpha(colors.accent, 0.45)} 60% 92%, transparent 92%)`,
    padding: `0 ${Math.round(12 * u)}px`,
  };
}

interface Built {
  cover: { w: number; h: number; frame: ReturnType<typeof cardCoverFrame> };
  /** The column items at a top-left of (0, 0). */
  column: KitCardItem[];
  columnW: number;
  columnH: number;
  gap: number;
}

/** The row at one scale: the cover and the column, placed at (0, 0). */
function buildRow(input: KitCardLayoutInput<KitBannerCard>, site: BannerSiteSpec, scale: number): Built {
  const { card, themeId, colors } = input;
  const id = storyThemeIdOf(themeId);
  const u = site.unit * scale;
  const { factors } = STORY_FRAMES[id];
  const display: KitCardFont = { id: input.fonts.display, style: input.typeStyle.display };
  const text: KitCardFont = { id: input.fonts.text, style: input.typeStyle.text };
  const frame = cardCoverFrame(themeId, colors, coverSpec(u));
  const coverW = Math.round((id === 'handmade' ? BANNER_SIZES.coverHandmade : BANNER_SIZES.cover) * u);
  const coverH = frame.heightOf(coverW);
  const gap = Math.round(BANNER_SIZES.rowGap * u) + (id === 'retro' ? Math.round(BANNER_SIZES.retroRecordRoom * u) : 0);
  const columnRoom = site.box.w - coverW - gap;
  const columnGap = Math.round((id === 'handmade' ? BANNER_SIZES.columnGapHandmade : BANNER_SIZES.columnGap) * u);

  const statusSize = BANNER_SIZES.status * u * factors.text;
  const chip = {
    padX: Math.round(BANNER_SIZES.statusPadX * u),
    padY: Math.round(BANNER_SIZES.statusPadY * u),
    border: Math.round(BANNER_SIZES.statusBorder * u),
  };
  const statusFit = fitCardText(card.status, text, statusSize, columnRoom - 2 * (chip.padX + chip.border));
  const markerPad = id === 'handmade' ? 2 * Math.round(12 * u) : 0;
  const titleFit = fitCardText(
    card.title,
    display,
    BANNER_SIZES.title * u * factors.display * input.typeStyle.display.scale,
    columnRoom - markerPad,
    { maxLines: 2 }
  );
  const artistFit = fitCardText(card.artist, text, BANNER_SIZES.artist * u * factors.text, columnRoom);
  const urlFont: KitCardFont = { id: text.id, style: { ...text.style, weight: 500, case: 'none', tracking: 0.02 } };
  const urlLine = site.url && card.link !== null ? `${card.linkLead} ${card.link}` : null;
  const urlFit =
    urlLine === null ? null : fitCardText(urlLine, urlFont, BANNER_SIZES.url * u * factors.text, columnRoom);

  const parts: Array<(top: number) => KitCardItem> = [
    (top) =>
      cardText({
        role: 'status',
        x: 0,
        y: top,
        lines: statusFit.lines,
        sizePx: statusFit.sizePx,
        lineHeight: LINE.text,
        font: text,
        align: 'center',
        chip,
        css: { ...cardTypeCss(text.style), ...statusCss(themeId, colors, u) },
      }),
    (top) => {
      const item = cardText({
        role: 'title',
        x: 0,
        y: top,
        lines: titleFit.lines,
        sizePx: titleFit.sizePx,
        lineHeight: LINE.display,
        font: display,
        align: 'left',
        css: { ...cardTypeCss(display.style), ...titleCss(themeId, colors, u) },
        lineCss: titleLineCss(themeId, colors, u),
      });
      return markerPad === 0 ? item : { ...item, box: { ...item.box, w: round2(item.box.w + markerPad) } };
    },
  ];
  if (id === 'dark') {
    // The Noir accent line under the title.
    const line = {
      w: Math.round(BANNER_SIZES.noirLine.w * u),
      h: Math.max(2, Math.round(BANNER_SIZES.noirLine.h * u)),
    };
    parts.push((top) => ({
      kind: 'shape',
      role: 'line',
      box: { x: 0, y: round2(top), w: line.w, h: line.h },
      css: { background: colors.accent },
    }));
  }
  parts.push((top) =>
    cardText({
      role: 'artist',
      x: 0,
      y: top,
      lines: artistFit.lines,
      sizePx: artistFit.sizePx,
      lineHeight: LINE.text,
      font: text,
      align: 'left',
      css: { ...cardTypeCss(text.style), color: colors.text },
    })
  );
  const { link } = card;
  if (urlFit !== null && link !== null) {
    parts.push((top) =>
      cardText({
        role: 'url',
        x: 0,
        y: top,
        lines: urlFit.lines,
        sizePx: urlFit.sizePx,
        lineHeight: LINE.text,
        font: urlFont,
        align: 'left',
        css: { ...cardTypeCss(urlFont.style), color: colors.text },
        boldTail: link,
      })
    );
  }
  if (input.watermark === true) {
    // Design w0: a row lockup, L 56 times the unit of the site; the logo alone when the column is narrow.
    parts.push((top) =>
      cardWatermark({
        x: 0,
        y: top,
        form: WATERMARK_PLACEMENTS.banner.form,
        L: WATERMARK_PLACEMENTS.banner.L * u,
        maxWidth: columnRoom,
        align: 'left',
      })
    );
  }
  const column: KitCardItem[] = [];
  let top = 0;
  parts.forEach((part, index) => {
    const item = part(top);
    column.push(item);
    top = item.box.y + item.box.h + (index < parts.length - 1 ? columnGap : 0);
  });
  const columnW = Math.min(columnRoom, Math.max(...column.map((item) => item.box.w)));
  return { cover: { w: coverW, h: coverH, frame }, column, columnW, columnH: top, gap };
}

/**
 * The layout of one banner: the row (cover, gap, column) centred in the content box of the site.
 * When the column is taller than the box, the whole row shrinks, down to 60 %. Pure.
 */
export function bannerLayout(input: KitCardLayoutInput<KitBannerCard>): KitCardLayout {
  const site = BANNER_SITES[input.card.site];
  const fits = (built: Built): boolean =>
    built.columnH <= site.box.h + 0.01 && Math.max(built.cover.h, 0) <= site.box.h + 0.01;
  let scale = 1;
  let built = buildRow(input, site, scale);
  if (!fits(built)) {
    let low = MIN_SCALE;
    let high = 1;
    for (let step = 0; step < 20 && high - low > 0.005; step += 1) {
      const middle = (low + high) / 2;
      if (fits(buildRow(input, site, middle))) {
        low = middle;
      } else {
        high = middle;
      }
    }
    scale = Math.floor(low * 1000) / 1000;
    built = buildRow(input, site, scale);
  }
  const rowW = built.cover.w + built.gap + built.columnW;
  const left = site.box.x + (site.box.w - rowW) / 2;
  const coverTop = site.box.y + (site.box.h - built.cover.h) / 2;
  const columnLeft = left + built.cover.w + built.gap;
  const columnTop = site.box.y + (site.box.h - built.columnH) / 2;
  const { frame } = built.cover;
  const items: KitCardItem[] = [
    {
      kind: 'cover',
      box: { x: round2(left), y: round2(coverTop), w: built.cover.w, h: round2(built.cover.h) },
      outer: frame.outer,
      picture: frame.picture,
      tape: frame.tape,
      record: frame.record,
    },
    ...built.column.map((item) => ({
      ...item,
      box: { ...item.box, x: round2(columnLeft + item.box.x), y: round2(columnTop + item.box.y) },
    })),
  ];
  return {
    size: site.size,
    ground: bannerGround(input.themeId, input.colors),
    deco: bannerDeco(input.themeId, input.colors, input.card.site),
    items,
  };
}
